/**
 * CLIENTE HTTP PARA LA API - HANAMIPOS FRONTEND
 *
 * Este archivo configura Axios como cliente HTTP para comunicarse con el backend Django.
 * Incluye interceptors para manejo automático de tokens JWT y errores.
 *
 * CARACTERÍSTICAS:
 * - Configuración base de Axios
 * - Interceptors para tokens JWT automáticos
 * - Refresh automático de tokens
 * - Manejo centralizado de errores
 * - Logging en modo desarrollo
 */

import axios from 'axios'

/**
 * CONFIGURACIÓN BASE DEL CLIENTE HTTP
 *
 * Creamos una instancia personalizada de Axios con configuración específica
 */
const api = axios.create({
  // URL base de la API desde variables de entorno
  baseURL: import.meta.env.VITE_API_BASE_URL,

  // Timeout para requests (10 segundos)
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 10000,

  // Headers por defecto
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
})

/**
 * FUNCIONES HELPER PARA TOKENS
 *
 * Estas funciones manejan el almacenamiento y recuperación de tokens JWT
 */

// Obtener token de acceso del localStorage
const getAccessToken = () => {
  return localStorage.getItem(import.meta.env.VITE_JWT_ACCESS_TOKEN_KEY || 'access_token')
}

// Obtener refresh token del localStorage
const getRefreshToken = () => {
  return localStorage.getItem(import.meta.env.VITE_JWT_REFRESH_TOKEN_KEY || 'refresh_token')
}

// Guardar tokens en localStorage
const setTokens = (accessToken, refreshToken) => {
  if (accessToken) {
    localStorage.setItem(import.meta.env.VITE_JWT_ACCESS_TOKEN_KEY || 'access_token', accessToken)
  }
  if (refreshToken) {
    localStorage.setItem(import.meta.env.VITE_JWT_REFRESH_TOKEN_KEY || 'refresh_token', refreshToken)
  }
}

// Limpiar tokens del localStorage
const clearTokens = () => {
  localStorage.removeItem(import.meta.env.VITE_JWT_ACCESS_TOKEN_KEY || 'access_token')
  localStorage.removeItem(import.meta.env.VITE_JWT_REFRESH_TOKEN_KEY || 'refresh_token')
  localStorage.removeItem('user_data')
}

// Verificar si el token está expirado
const isTokenExpired = (token) => {
  if (!token) return true

  try {
    // Decodificar el payload del JWT (sin verificar la firma)
    const payload = JSON.parse(atob(token.split('.')[1]))
    const now = Math.floor(Date.now() / 1000)

    // Verificar si el token está expirado (con 1 minuto de margen)
    return payload.exp < (now + 60)
  } catch (error) {
    console.error('Error al decodificar token:', error)
    return true
  }
}

/**
 * INTERCEPTOR DE REQUEST
 *
 * Se ejecuta ANTES de cada request para agregar el token de autenticación
 */
api.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken()

    // Agregar token Bearer si existe y no está expirado
    if (accessToken && !isTokenExpired(accessToken)) {
      config.headers.Authorization = `Bearer ${accessToken}`

      // MULTI-TENANT: Si el usuario es superuser, agregar header de organización
      // (Para casos donde un superuser administra múltiples organizaciones)
      try {
        const userData = JSON.parse(localStorage.getItem('user_data') || '{}')
        const organizationData = JSON.parse(localStorage.getItem('organization_data') || '{}')

        if (userData.is_superuser && organizationData.id) {
          config.headers['X-Organization-ID'] = organizationData.id
        }
      } catch (error) {
        console.warn('⚠️ Error leyendo datos de usuario/organización:', error)
      }
    }

    // Log en modo desarrollo
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.log(`🔗 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        data: config.data,
        params: config.params,
        hasAuth: !!config.headers.Authorization,
        orgHeader: config.headers['X-Organization-ID'] || null
      })
    }

    return config
  },
  (error) => {
    console.error('❌ Error en interceptor de request:', error)
    return Promise.reject(error)
  }
)

/**
 * INTERCEPTOR DE RESPONSE
 *
 * Se ejecuta DESPUÉS de cada response para manejar errores y refresh de tokens
 */
api.interceptors.response.use(
  (response) => {
    // Log en modo desarrollo
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`, {
        data: response.data
      })
    }

    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Log del error
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.error(`❌ API Error: ${error.response?.status} ${error.config?.url}`, {
        error: error.response?.data,
        status: error.response?.status
      })
    }

    // MULTI-TENANT: Manejo específico de errores multi-tenant (403)
    if (error.response?.status === 403) {
      const errorMessage = error.response.data?.detail || error.response.data?.message || ''

      // Organización desactivada
      if (errorMessage.includes('desactivada') || errorMessage.toLowerCase().includes('deactivated')) {
        console.error('🚫 Organización desactivada')
        window.dispatchEvent(new CustomEvent('organization:deactivated', {
          detail: { message: errorMessage }
        }))
        clearTokens()
        return Promise.reject(error)
      }

      // Trial expirado
      if (errorMessage.includes('trial') || errorMessage.includes('prueba') || errorMessage.includes('expirado')) {
        console.warn('⚠️ Trial expirado')
        window.dispatchEvent(new CustomEvent('organization:trial-expired', {
          detail: { message: errorMessage }
        }))
        // NO cerrar sesión, solo mostrar advertencia
        return Promise.reject(error)
      }
    }

    // Si es error 401 (no autorizado) y no hemos intentado renovar aún
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== '/auth/token/refresh/'
    ) {
      originalRequest._retry = true

      try {
        // Intentar renovar el token
        const refreshToken = getRefreshToken()

        if (!refreshToken) {
          throw new Error('No hay refresh token disponible')
        }

        if (import.meta.env.VITE_DEBUG === 'true') {
          console.log('🔄 Intentando renovar token...')
        }

        // Hacer request para renovar token
        const refreshResponse = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL}/auth/token/refresh/`,
          { refresh: refreshToken },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 5000 // Timeout corto para refresh
          }
        )

        const { access } = refreshResponse.data

        // Guardar nuevo token
        setTokens(access, refreshToken)

        // Actualizar header del request original
        originalRequest.headers.Authorization = `Bearer ${access}`

        if (import.meta.env.VITE_DEBUG === 'true') {
          console.log('✅ Token renovado exitosamente')
        }

        // Reintentar request original
        return api(originalRequest)

      } catch (refreshError) {
        if (import.meta.env.VITE_DEBUG === 'true') {
          console.error('❌ Error al renovar token:', refreshError)
        }

        // Si falló el refresh, limpiar tokens y redirigir a login
        clearTokens()

        // Emitir evento personalizado para que la app maneje el logout
        window.dispatchEvent(new CustomEvent('auth:token-expired'))

        return Promise.reject(refreshError)
      }
    }

    // Para otros errores, rechazar normalmente
    return Promise.reject(error)
  }
)

/**
 * FUNCIONES HELPER PARA REQUESTS COMUNES
 *
 * Estas funciones simplifican operaciones comunes de la API
 */

// Login con PIN
export const loginWithPin = async (pin, startSession = false, startingCash = 0) => {
  try {
    const response = await api.post('/auth/pin-login/', {
      pin,
      start_session: startSession,
      starting_cash: startingCash
    })

    const { access, refresh, user } = response.data

    // Guardar tokens y datos del usuario
    setTokens(access, refresh)
    localStorage.setItem('user_data', JSON.stringify(user))

    return response.data
  } catch (error) {
    console.error('Error en login:', error)
    throw error
  }
}

// Logout
export const logout = async (closeSession = false, endingCash = 0, notes = '') => {
  try {
    const refreshToken = getRefreshToken()

    if (refreshToken) {
      await api.post('/auth/logout/', {
        refresh_token: refreshToken,
        close_work_session: closeSession,
        ending_cash: endingCash,
        notes: notes
      })
    }
  } catch (error) {
    console.error('Error en logout:', error)
    // Continuar con el logout local aunque falle el servidor
  } finally {
    // Siempre limpiar tokens localmente
    clearTokens()
  }
}

// Refresh manual de token
export const refreshToken = async () => {
  const refreshTokenValue = getRefreshToken()

  if (!refreshTokenValue) {
    throw new Error('No hay refresh token disponible')
  }

  const response = await api.post('/auth/token/refresh/', {
    refresh: refreshTokenValue
  })

  const { access } = response.data
  setTokens(access, refreshTokenValue)

  return access
}

/**
 * FUNCIONES PARA MANEJAR ERRORES DE FORMA CONSISTENTE
 */

// Extraer mensaje de error de la respuesta
export const getErrorMessage = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message
  }

  if (error.response?.data?.detail) {
    return error.response.data.detail
  }

  if (error.response?.data?.error) {
    return error.response.data.error
  }

  if (error.message) {
    return error.message
  }

  // Mensajes por código de estado
  const statusMessages = {
    400: 'Datos inválidos',
    401: 'No autorizado',
    403: 'Sin permisos',
    404: 'No encontrado',
    500: 'Error del servidor',
    503: 'Servicio no disponible'
  }

  return statusMessages[error.response?.status] || 'Error desconocido'
}

// Verificar si el error es de conexión
export const isNetworkError = (error) => {
  return !error.response && (error.code === 'NETWORK_ERROR' || error.message === 'Network Error')
}

/**
 * EXPORTACIONES
 */

// Cliente API principal
export default api

// Funciones de tokens
export {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  isTokenExpired
}

// Event listener para debug
if (import.meta.env.VITE_DEBUG === 'true') {
  window.addEventListener('auth:token-expired', () => {
    console.log('🚪 Token expirado - Usuario deslogueado')
  })
}